/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CommentSyncService {
  public commentCounts = new Map<string, BehaviorSubject<number>>();

  /**
   * Derniere valeur connue par noeud, doublant les sujets.
   *
   * Pourquoi ce doublon : le gabarit de la liste a besoin d'une lecture synchrone, a chaque cycle de
   * detection de changements et pour chaque ligne. Lire `.value` d'un BehaviorSubject est interdit
   * par la regle `rxjs/no-subject-value`, et s'y abonner fuyait un abonnement par appel.
   */
  private readonly lastKnownCounts = new Map<string, number>();

  getCommentCount(nodeId: string) {
    if (!this.commentCounts.has(nodeId)) {
      this.commentCounts.set(nodeId, new BehaviorSubject<number>(0));
      this.lastKnownCounts.set(nodeId, 0);
    }
    return this.commentCounts.get(nodeId)?.asObservable();
  }

  /** Valeur courante, sans abonnement ni creation de sujet. A l'usage des gabarits. */
  peekCommentCount(nodeId: string): number {
    return this.lastKnownCounts.get(nodeId) ?? 0;
  }

  updateCommentCount(nodeId: string, newCount: number) {
    this.lastKnownCounts.set(nodeId, newCount);

    // On pousse dans le sujet existant plutot que de le remplacer : un abonne garde sa source.
    if (!this.commentCounts.has(nodeId)) {
      this.commentCounts.set(nodeId, new BehaviorSubject<number>(newCount));
    } else {
      this.commentCounts.get(nodeId)?.next(newCount);
    }
  }
}
